import BraintreeClient from './BraintreeClient';
import BraintreeHostedFields from './BraintreeHostedFields';

export default class BraintreeCardPayment extends BraintreeClient {
	constructor() {
		super();
		this.form = document.getElementById('billingForm');
		this.submitButtonId = 'billingSubmit';
		this.errorMessageContainer = document.getElementById(
			'braintree-error-message-container',
		);
		this.threeDSecure = null;
		this.deviceData = '';
		this.hostedFields = null;
		this.isAsmAgentLoggedIn = this.navigation.isAsmAgentLoggedIn;
		this.init();
	}

	async init() {
		try {
			const client = await this.createClient();
			this.threeDSecure = await this.braintree.threeDSecure.create({
				authorization: this.paymentToken,
				version: 2,
			});
			const {
				rawDeviceData: { correlation_id: deviceData },
			} = await this.braintree.dataCollector.create({
				client,
			});
			this.deviceData = deviceData;
			this.hostedFields = await new BraintreeHostedFields(
				client,
				this.braintree.hostedFields,
				this.isAsmAgentLoggedIn,
			).create();
			this.form.addEventListener(
				'hide-error-message',
				this.hideErrorMessage.bind(this),
			);
			this.form.addEventListener('show-error-message', ({ detail }) => {
				this.showErrorMessage(detail);
			});

			// Expose the payment logic for the BillingFormController
			window.performBraintreeCardPayment = this.performCardPayment.bind(this);
		} catch (error) {
			console.error('[Braintree Card Payment error]:', error); // eslint-disable-line no-console
		}
	}

	hideErrorMessage() {
		this.errorMessageContainer.setAttribute('hidden', '');
	}

	showErrorMessage(id) {
		const { innerHTML: errorMessage } = document.getElementById(id);
		const errorMessageElement = document.getElementById(
			'braintree-error-message',
		);
		errorMessageElement.innerHTML = errorMessage;
		this.errorMessageContainer.removeAttribute('hidden');
	}

	/**
	 * This function is called by BillingFormController.
	 * It only handles the Braintree Card-specific logic.
	 * @returns {Promise<boolean>} - Resolves true on success, false on failure.
	 */
	async performCardPayment() {
		try {
			const isValid = this.validateBraintreeForm(this.hostedFields);
			if (!isValid) {
				return false;
			}

			this.hideErrorMessage();

			let { nonce, details } = await this.hostedFields.tokenize(); // eslint-disable-line prefer-const

			let threeDSecurePayload;
			if (!this.isAsmAgentLoggedIn) {
				const threeDSecureParameters = {
					onLookupComplete: (data, next) => {
						next();
					},
					amount: this.amount,
					nonce,
					bin: details.bin,
					email: this.dataLayer.userInfo.userId,
					challengeRequested: true,
				};
				threeDSecurePayload = await this.threeDSecure.verifyCard(
					threeDSecureParameters,
				);
				nonce = threeDSecurePayload.nonce;
			}

			if (!threeDSecurePayload?.liabilityShifted && !this.isAsmAgentLoggedIn) {
				this.showErrorMessage('threeds-check-error-message');
				return false; // 3DS failed
			}

			const deviceDataHiddenInput = this.createHiddenInput(
				'deviceData',
				this.deviceData,
			);
			this.form.appendChild(deviceDataHiddenInput);

			const eciFlagHiddenInput = this.createHiddenInput(
				'eciFlag',
				threeDSecurePayload.threeDSecureInfo?.eciFlag || null,
			);
			this.form.appendChild(eciFlagHiddenInput);

			this.submitFormWithBraintreeNonce(this.form, nonce);
			return true;
		} catch (error) {
			this.showErrorMessage('general-error-message');
			console.error('[Braintree Card Payment form submission error]:', error); // eslint-disable-line no-console
			return false;
		}
	}

	getPaymentMethod(form) {
		return form.querySelector('input[name="paymentMethod"]:checked')?.value;
	}

	validateBraintreeForm(instance) {
		const { fields } = instance.getState();
		Object.keys(fields).forEach(key => {
			if (!fields[key].isValid) {
				const { classList } = fields[key].container;
				classList.add('is-invalid');
			}
		});
		const phone = document.querySelector('input[name="phone"]');
		const isPhoneValid =
			!phone ||
			(!phone.classList.contains('is-invalid') && phone.value.trim() !== '');

		const invoiceEmail = document.querySelector(
			'input[name="invoiceEmailAddress"]',
		);
		const isInvoiceEmailValid =
			!invoiceEmail || !invoiceEmail.classList.contains('is-invalid');

		return (
			Object.values(fields).every(field => field.isValid) &&
			isPhoneValid &&
			isInvoiceEmailValid
		);
	}
}
